import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { global } from "@shared/libs/Global";
import { type VarProps } from "@widgets/commonpopup/VarPropsPopup";

interface Props {
  /** 양식 HTML 원문 */
  body: string;
  /** 원문 변경 콜백 */
  setBody: (val: string) => void;
  /**
   * 이 양식에 등록된 변수 속성(변수속성 컨트롤에 입력한 값).
   * 툴바의 "변수" 목록으로 뜨고, 고르면 커서 위치에 토큰이 꽂힌다.
   */
  variables?: VarProps[];
}

/**
 * 본문에 꽂히는 치환 토큰 형식.
 * 결재양식이 예전부터 쓰던 표기라 그대로 따른다 — 예: @%CD_DEPT@%
 */
export const toToken = (id: string) => `@%${id}@%`;

/**
 * 날짜 포맷을 적용하지 않는 Type. 이 값들은 Format이 있어도 Default를 그대로 쓴다.
 * Type 목록은 공통코드(SYS205)라 회사마다 코드값이 다를 수 있어서, "날짜인 것"을 열거하는 대신
 * "날짜가 아닌 게 확실한 것"만 막는다. 그래야 date/DATE/날짜 중 뭘 등록했든 동작한다.
 */
const NON_DATE_TYPES = ["number", "boolean", "int", "decimal"];

/**
 * Default에 넣으면 고정값이 아니라 "볼 때마다 계산되는" 값이 되는 예약어.
 *
 * 부서명/이름/직위는 로그인한 사람 기준이고, 오늘날짜는 여는 날 기준이다.
 * 여기 없는 값(시작기간·마감기간처럼 직접 지정하는 날짜)은 입력한 그대로 쓴다.
 */
const USER = () => global.getState().userInfo ?? {};

/**
 * 같은 뜻으로 쓰는 표현을 모두 받아준다. "오늘날짜"만 받으면 "오늘"이라고 적었을 때
 * 예약어로 안 잡히고 글자 그대로 찍혀서, 왜 안 되는지 알기 어렵다.
 * 각 그룹의 첫 번째가 대표 표기(툴바 도움말에 뜨는 이름)다.
 */
export const DYNAMIC_DEFAULT_GROUPS: { keys: string[]; get: () => string }[] = [
  { keys: ["부서명", "부서", "부서이름"], get: () => USER().nm_dept ?? "" },
  { keys: ["이름", "성명", "사원명"], get: () => USER().nm_emp ?? "" },
  // 양식 본문이 쓰던 @%FG_ROLE@%에 맞춰 fg_role의 명칭을 쓴다.
  // 직위를 fg_pos로 관리하는 회사면 nm_fg_pos로 바꾸면 된다.
  { keys: ["직위", "직책"], get: () => USER().nm_fg_role ?? "" },
  {
    keys: ["오늘날짜", "오늘", "금일", "today", "TODAY"],
    get: () => dayjs().format("YYYYMMDD"),
  },
];

const DYNAMIC_DEFAULTS: Record<string, () => string> = Object.fromEntries(
  DYNAMIC_DEFAULT_GROUPS.flatMap((g) => g.keys.map((k) => [k, g.get]))
);

/** 툴바 도움말에 뿌릴 대표 예약어 목록 */
const DYNAMIC_DEFAULT_KEYS = DYNAMIC_DEFAULT_GROUPS.map((g) => g.keys[0]).join(
  ", "
);

/**
 * 변수 하나가 본문에 꽂힐 최종 문자열.
 *
 * Format이 비어 있으면 Default 그대로. Format이 있으면 dayjs 표기로 보고 날짜 포맷을 적용한다.
 * Default를 날짜로 못 읽으면(예: 그냥 문자열) 원래 값을 그대로 둔다 — 깨진 값을 만들지 않기 위함.
 * 프로젝트 날짜 표기가 YYYYMMDD 8자리인데, dayjs 기본 파서가 이 형태를 그대로 읽는다.
 */
const resolveValue = (v: VarProps): string => {
  const raw = (v.default ?? "").trim();
  const fmt = (v.format ?? "").trim();
  if (!raw) return "";

  // 예약어면 지금 값으로 바꾸고, 아니면 입력한 값을 그대로 쓴다
  const value = DYNAMIC_DEFAULTS[raw]?.() ?? raw;
  if (!value || !fmt) return value;
  if (NON_DATE_TYPES.includes((v.type ?? "").trim().toLowerCase())) return value;

  // 날짜로 읽히면 Format을 적용하고, 아니면(부서명·이름 등) 그대로 둔다
  const d = dayjs(value);
  return d.isValid() ? d.format(fmt) : value;
};

/**
 * 본문의 @%ID@% 토큰을 변수속성 값으로 바꿔 돌려준다.
 *
 * 원문(js_body)은 토큰을 그대로 유지해야 재사용이 되므로, 저장값은 절대 건드리지 않고
 * "보여줄 때"만 이 함수를 통과시킨다. 미리보기가 그 첫 사용처이고,
 * 나중에 결재 상신/출력 화면이 생기면 같은 함수에 실제 값을 넣어 쓰면 된다.
 */
export const applyVarProps = (html: string, vars?: VarProps[]): string =>
  (vars ?? []).reduce((acc, v) => {
    const id = v?.id?.trim();
    if (!id) return acc;
    // ID에 정규식 메타문자(. * + 등)가 섞여도 글자 그대로 찾도록 이스케이프한다
    const escaped = id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return acc.replace(new RegExp(`@%${escaped}@%`, "g"), resolveValue(v));
  }, html || "");

/**
 * 편집 iframe 안에서 스크립트는 실행되지 않도록 제거 (신뢰된 양식이라도 방어적으로).
 * 호출부가 문자열을 주는 게 원칙이지만, jsonb가 객체로 내려오는 경로가 있어
 * 문자열이 아닌 값이 들어와도 터지지 않게 막는다.
 */
const stripScripts = (html: any) =>
  typeof html === "string"
    ? html.replace(/<script[\s\S]*?<\/script>/gi, "")
    : "";

/** 편집 편의를 위해 iframe 문서에 끼워넣는 요소 표시. 저장 시 emitFromIframe이 걷어낸다. */
const EDITOR_ONLY_ATTR = "data-sn-editor-only";

/**
 * 편집을 위해 contenteditable을 손댄 요소에, 원래 값을 적어두는 자리.
 * 저장할 때 이 값으로 되돌려 원문을 그대로 유지한다(없었으면 속성 자체를 지운다).
 */
const CE_ORIG_ATTR = "data-sn-ce-orig";

/**
 * designMode를 켜도 contenteditable="false"가 걸린 요소 안은 편집이 막힌다.
 * 레거시 결재양식은 @%토큰@% 자리를 이렇게 잠가두는 경우가 있어, 편집 중에만 풀어준다.
 */
const unlockEditable = (root: Document | HTMLElement) => {
  root.querySelectorAll("[contenteditable]").forEach((el) => {
    if ((el.getAttribute("contenteditable") ?? "").toLowerCase() === "true") {
      return;
    }
    if (!el.hasAttribute(CE_ORIG_ATTR)) {
      el.setAttribute(CE_ORIG_ATTR, el.getAttribute("contenteditable") ?? "");
    }
    el.setAttribute("contenteditable", "true");
  });
};

/**
 * 편집 중에만 적용하는 보정 CSS. 저장 시 통째로 걷어내므로 원문에 남지 않는다.
 *
 * - min-height : 본문이 짧으면 클릭 영역이 첫 줄뿐이라 아래를 눌러도 커서가 안 잡힌다.
 * - user-select: 인쇄용 레거시 양식이 특정 구간에 user-select:none을 걸어두면
 *                선택 자체가 안 돼 캐럿을 못 놓는다. 편집 중에는 전부 풀어준다.
 */
const EDITOR_ONLY_CSS = [
  "html,body{min-height:100%;box-sizing:border-box;}",
  "*{user-select:text !important;-webkit-user-select:text !important;}",
].join("");

const FONT_FAMILIES = [
  "돋움",
  "굴림",
  "바탕",
  "맑은 고딕",
  "Arial",
  "Times New Roman",
];

/**
 * 양식 HTML 템플릿 편집기 (툴바 위지윅).
 *
 * 결재양식 같은 인쇄 템플릿은 전체 문서형 HTML(@%토큰@%, <head><style>, 레이아웃 <table>)이라
 * CKEditor 5(스키마 모델)에 넣으면 구조가 벗겨진다. 그래서 브라우저가 원본을 그대로 렌더하는
 * iframe을 designMode로 만들어 편집하고, 그 위에 document.execCommand 기반 툴바를 얹는다.
 * (레거시 HTML 에디터가 쓰던 방식 — 임의의 HTML을 벗기지 않고 서식 편집)
 * - "편집" 탭 : 툴바 + iframe. 그려진 양식 위에서 서식/텍스트 편집.
 * - "HTML" 탭 : raw HTML textarea. 원문 붙여넣기/수정.
 */
const SnHtmlTemplateEditor: React.FC<Props> = ({
  body,
  setBody,
  variables,
}) => {
  const [mode, setMode] = useState<"edit" | "html" | "preview">("edit");
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const lastEmittedRef = useRef<string | null>(null); // iframe→부모로 올린 마지막 값
  const loadedRef = useRef<string | null>(null); // iframe에 로드된 값
  const savedRangeRef = useRef<Range | null>(null); // 툴바 조작 시 복원할 선택영역

  const getDoc = () => iframeRef.current?.contentDocument || null;
  const getWin = () => iframeRef.current?.contentWindow || null;

  /**
   * 지금 iframe 내용을 저장용 HTML 문자열로 만든다 (부모로 올리지는 않는다).
   *
   * 편집을 위해 넣은 것들(높이 보정 style, contenteditable 손댄 것)은 양식 원문이 아니라
   * 복제본에서 걷어낸다.
   */
  const buildHtml = (): string | null => {
    const doc = getDoc();
    if (!doc?.documentElement) return null;

    const clone = doc.documentElement.cloneNode(true) as HTMLElement;
    clone
      .querySelectorAll(`[${EDITOR_ONLY_ATTR}]`)
      .forEach((el) => el.remove());

    // 우리가 손댄 것만 원래 값으로 되돌린다. 원문에 있던 contenteditable="false"는
    // 그대로 살려야 하므로 통째로 지우지 않는다.
    clone.querySelectorAll(`[${CE_ORIG_ATTR}]`).forEach((el) => {
      const orig = el.getAttribute(CE_ORIG_ATTR) ?? "";
      el.removeAttribute(CE_ORIG_ATTR);
      if (orig) el.setAttribute("contenteditable", orig);
      else el.removeAttribute("contenteditable");
    });

    return "<!DOCTYPE html>\n" + clone.outerHTML;
  };

  /**
   * 문서를 막 실어놓은 직후의 HTML. "사용자가 실제로 고쳤는지" 판단하는 기준선이다.
   *
   * 브라우저는 document.write로 실을 때 원문을 정규화한다(<tbody> 삽입, 속성 표기 정리 등).
   * 그래서 아무것도 안 고쳐도 원문(body)과 iframe에서 다시 뽑은 HTML은 다르다.
   * 원문과 비교하면 클릭만 해도 "수정됨"이 되므로, 정규화까지 끝난 이 값과 비교한다.
   */
  const baselineRef = useRef<string | null>(null);

  // iframe 편집 내용을 부모(formData)로 반영
  const emitFromIframe = () => {
    const html = buildHtml();
    if (html == null) return;

    // 실제로 바뀐 게 없으면 올리지 않는다. 올리면 SharedForm이 그리드 행을
    // 수정됨으로 표시해서(SharedForm.tsx:170 sharedCellValueChanged),
    // 손대지 않았는데도 조회할 때 "수정된 내용이 있습니다"가 뜬다.
    if (html === baselineRef.current) return;

    baselineRef.current = html;
    lastEmittedRef.current = html;
    setBody(html);
  };

  // loadDoc이 등록하는 blur 리스너는 등록 시점의 setBody(=그 시점 formData)를 붙잡는다.
  // 문서를 다시 로드하지 않는 한 리스너가 갱신되지 않아, 행을 선택하기 전의 빈 formData로
  // 값을 올려보내는 문제가 있었다. 항상 최신 emit을 타도록 ref로 우회한다.
  const emitRef = useRef(emitFromIframe);
  emitRef.current = emitFromIframe;

  // 현재 iframe 선택영역 저장 (툴바의 select/color가 포커스를 가져가도 복원할 수 있게)
  const saveSelection = () => {
    const sel = getWin()?.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const win = getWin();
    const sel = win?.getSelection();
    win?.focus();
    if (sel && savedRangeRef.current) {
      sel.removeAllRanges();
      sel.addRange(savedRangeRef.current);
    }
  };

  // 서식 명령 실행
  const exec = (command: string, value?: string) => {
    const doc = getDoc();
    if (!doc) return;
    restoreSelection();
    try {
      doc.execCommand(command, false, value);
    } catch {
      /* 일부 브라우저에서 미지원 명령 무시 */
    }
    saveSelection();
    emitFromIframe();
  };

  // ID 없는 행은 토큰을 만들 수 없으므로 목록에서 뺀다
  const varOptions = (variables ?? []).filter((v) => v?.id?.trim());

  /**
   * 커서 위치에 변수 토큰을 꽂는다.
   * 서식 명령과 같은 exec 경로를 타므로 선택영역 복원 → 삽입 → 부모 반영이 한 번에 끝난다.
   * insertHTML이 아니라 insertText라 ID에 <, & 같은 문자가 섞여도 그대로 글자로 들어간다.
   */
  const insertVariable = (id: string) => {
    if (!id) return;
    exec("insertText", toToken(id));
  };

  /**
   * 문서를 편집 가능 상태로 만든다.
   *
   * designMode는 문서가 아직 준비 중이면 조용히 먹지 않는 경우가 있어서,
   * body에 contenteditable을 함께 걸어 이중으로 보장한다.
   * 원문에 contenteditable="false"로 잠긴 구간(@%토큰@% 자리 등)도 여기서 풀어준다.
   * (손댄 속성은 emitFromIframe이 저장 전에 원래 값으로 되돌린다)
   */
  const enableEditing = () => {
    const doc = getDoc();
    if (!doc) return;
    try {
      doc.designMode = "on";
    } catch {
      /* noop */
    }
    if (doc.body && !doc.body.isContentEditable) {
      if (!doc.body.hasAttribute(CE_ORIG_ATTR)) {
        doc.body.setAttribute(
          CE_ORIG_ATTR,
          doc.body.getAttribute("contenteditable") ?? ""
        );
      }
      doc.body.setAttribute("contenteditable", "true");
    }
    unlockEditable(doc);
    try {
      // 색/크기 등이 <font> 대신 style로 적용되도록
      doc.execCommand("styleWithCSS", false, "true");
    } catch {
      /* 일부 브라우저에서 미지원 명령 무시 */
    }
  };

  // iframe에 문서를 로드하고 편집 가능 상태로
  const loadDoc = (html: string) => {
    const doc = getDoc();
    if (!doc) return;
    loadedRef.current = html;
    doc.open();
    doc.write(stripScripts(html) || "<html><body></body></html>");
    doc.close();

    // 빈 본문에서도 프레임 어디를 눌러든 커서가 잡히도록 (저장 시 제거됨)
    const style = doc.createElement("style");
    style.setAttribute(EDITOR_ONLY_ATTR, "1");
    style.textContent = EDITOR_ONLY_CSS;
    (doc.head || doc.documentElement)?.appendChild(style);

    enableEditing();
    // close() 직후엔 아직 준비가 안 끝나 designMode가 안 걸리는 경우가 있어 한 번 더 건다
    requestAnimationFrame(enableEditing);

    // 정규화가 끝난 지금 상태를 "안 고친 상태"로 기억해 둔다.
    // 이후 emit은 이 값과 다를 때만 부모로 올라간다.
    baselineRef.current = buildHtml();

    doc.addEventListener("blur", () => emitRef.current(), true);
    doc.addEventListener("selectionchange", saveSelection);
    doc.addEventListener("mouseup", saveSelection);
    doc.addEventListener("keyup", saveSelection);
  };

  // 최초/외부(행 변경 등)로 body가 바뀌면 재로딩. 내부 편집분은 재로딩하지 않음(커서 튐 방지).
  useEffect(() => {
    if (body === lastEmittedRef.current) return;
    if (body === loadedRef.current) return;
    const id = requestAnimationFrame(() => loadDoc(body));
    return () => cancelAnimationFrame(id);
  }, [body]);

  const goHtml = () => {
    emitFromIframe();
    setMode("html");
  };
  const goEdit = () => {
    if (body !== loadedRef.current) loadDoc(body);
    // 다른 탭에 다녀오는 사이 designMode가 풀렸어도 다시 편집할 수 있게
    else requestAnimationFrame(enableEditing);
    setMode("edit");
  };
  // 편집 중인 내용까지 반영해서 보려면 먼저 iframe 내용을 올려보내야 한다
  const goPreview = () => {
    if (mode === "edit") emitFromIframe();
    setMode("preview");
  };

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: "3px 14px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    border: "1px solid rgb(186,191,199)",
    borderBottomColor: active ? "#fff" : "rgb(186,191,199)",
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    marginBottom: -1,
    background: active ? "#fff" : "#f2f4f7",
    color: active ? "#0b0c69" : "#555",
  });

  // 툴바 버튼: mousedown에서 preventDefault로 iframe 선택영역을 잃지 않게 함
  const TbBtn = ({
    cmd,
    value,
    title,
    children,
  }: {
    cmd: string;
    value?: string;
    title: string;
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => exec(cmd, value)}
      style={tbBtnStyle}
    >
      {children}
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* 탭 */}
      <div style={{ display: "flex", gap: 2, padding: "4px 6px 0", flex: "0 0 auto" }}>
        <button type="button" onClick={goEdit} style={tabBtnStyle(mode === "edit")}>
          편집
        </button>
        <button type="button" onClick={goHtml} style={tabBtnStyle(mode === "html")}>
          HTML
        </button>
        <button
          type="button"
          onClick={goPreview}
          title="변수 토큰을 변수속성의 Default 값으로 바꿔서 보여줍니다 (저장값은 그대로)"
          style={tabBtnStyle(mode === "preview")}
        >
          미리보기
        </button>
      </div>

      {/* 툴바 (편집 탭에서만) */}
      {mode === "edit" && (
        <div style={toolbarStyle}>
          <select
            title="글꼴"
            defaultValue=""
            onMouseDown={saveSelection}
            onChange={(e) => {
              exec("fontName", e.target.value);
              e.target.value = "";
            }}
            style={tbSelectStyle}
          >
            <option value="" disabled>
              글꼴
            </option>
            {FONT_FAMILIES.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>

          <select
            title="글자 크기"
            defaultValue=""
            onMouseDown={saveSelection}
            onChange={(e) => {
              exec("fontSize", e.target.value);
              e.target.value = "";
            }}
            style={tbSelectStyle}
          >
            <option value="" disabled>
              크기
            </option>
            {[1, 2, 3, 4, 5, 6, 7].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <span style={sepStyle} />

          <TbBtn cmd="bold" title="굵게">
            <b>B</b>
          </TbBtn>
          <TbBtn cmd="italic" title="기울임">
            <i>I</i>
          </TbBtn>
          <TbBtn cmd="underline" title="밑줄">
            <u>U</u>
          </TbBtn>
          <TbBtn cmd="strikeThrough" title="취소선">
            <s>S</s>
          </TbBtn>

          <label title="글자색" style={tbBtnStyle} onMouseDown={saveSelection}>
            <span style={{ fontWeight: 700, color: "#d00" }}>A</span>
            <input
              type="color"
              onChange={(e) => exec("foreColor", e.target.value)}
              style={colorInputStyle}
            />
          </label>
          <label title="배경색" style={tbBtnStyle} onMouseDown={saveSelection}>
            <span style={{ fontWeight: 700, background: "#ff0", padding: "0 2px" }}>A</span>
            <input
              type="color"
              onChange={(e) => exec("hiliteColor", e.target.value)}
              style={colorInputStyle}
            />
          </label>

          <span style={sepStyle} />

          <TbBtn cmd="justifyLeft" title="왼쪽 정렬">
            ≡
          </TbBtn>
          <TbBtn cmd="justifyCenter" title="가운데 정렬">
            ≡
          </TbBtn>
          <TbBtn cmd="justifyRight" title="오른쪽 정렬">
            ≡
          </TbBtn>
          <TbBtn cmd="insertUnorderedList" title="글머리 기호">
            •
          </TbBtn>
          <TbBtn cmd="insertOrderedList" title="번호 매기기">
            1.
          </TbBtn>
          <TbBtn cmd="outdent" title="내어쓰기">
            ⇤
          </TbBtn>
          <TbBtn cmd="indent" title="들여쓰기">
            ⇥
          </TbBtn>

          <span style={sepStyle} />

          {/* 변수 삽입 — 변수속성에 등록한 ID를 @%ID@% 토큰으로 커서 위치에 꽂는다.
              일반 사용자가 토큰 표기를 외워 타이핑하지 않아도 되게 하는 게 목적이다. */}
          <select
            title={
              varOptions.length
                ? `변수 삽입 — 커서 위치에 @%ID@% 토큰을 넣습니다.\n` +
                  `변수속성 Default에 다음을 넣으면 자동으로 채워집니다: ${DYNAMIC_DEFAULT_KEYS}`
                : "변수속성에 먼저 변수를 등록하세요"
            }
            defaultValue=""
            disabled={varOptions.length === 0}
            onMouseDown={saveSelection}
            onChange={(e) => {
              insertVariable(e.target.value);
              e.target.value = "";
            }}
            style={tbSelectStyle}
          >
            <option value="" disabled>
              {varOptions.length ? "변수" : "변수 없음"}
            </option>
            {varOptions.map((v) => (
              // ID명을 적어뒀으면 그걸 앞에 세운다 — 목록에서 고르기 쉬우라고.
              <option key={v.id} value={v.id}>
                {v.name?.trim() ? `${v.name} (${v.id})` : v.id}
              </option>
            ))}
          </select>

          <span style={sepStyle} />

          <button
            type="button"
            title="이미지 삽입(URL)"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              const url = window.prompt("이미지 URL을 입력하세요");
              if (url) exec("insertImage", url);
            }}
            style={tbBtnStyle}
          >
            🖼
          </button>
          <TbBtn cmd="removeFormat" title="서식 지우기">
            ⌫
          </TbBtn>
          <span style={sepStyle} />
          <TbBtn cmd="undo" title="실행 취소">
            ↶
          </TbBtn>
          <TbBtn cmd="redo" title="다시 실행">
            ↷
          </TbBtn>
        </div>
      )}

      {/* 본문 */}
      <div style={{ flex: "1 1 auto", minHeight: 0, position: "relative" }}>
        <iframe
          ref={iframeRef}
          title="form-template-editor"
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            background: "#fff",
            display: mode === "edit" ? "block" : "none",
          }}
        />
        {mode === "preview" && (
          // 편집용 iframe과 달리 designMode를 켜지 않아 읽기 전용이다.
          // srcDoc이라 부모 문서와 격리되고, 저장값(body)은 건드리지 않는다.
          <iframe
            title="form-template-preview"
            srcDoc={stripScripts(applyVarProps(body, variables))}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              background: "#fff",
            }}
          />
        )}
        {mode === "html" && (
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            spellCheck={false}
            style={{
              width: "100%",
              height: "100%",
              boxSizing: "border-box",
              padding: 8,
              border: "none",
              outline: "none",
              resize: "none",
              fontFamily: "monospace",
              fontSize: 12,
              lineHeight: 1.5,
              whiteSpace: "pre",
              overflow: "auto",
            }}
          />
        )}
      </div>
    </div>
  );
};

const toolbarStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 2,
  padding: "4px 6px",
  borderTop: "1px solid rgb(186,191,199)",
  borderBottom: "1px solid rgb(186,191,199)",
  background: "#fafbfc",
  flex: "0 0 auto",
};

const tbBtnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 26,
  height: 24,
  padding: "0 6px",
  fontSize: 12,
  lineHeight: 1,
  cursor: "pointer",
  border: "1px solid #d0d5dd",
  borderRadius: 3,
  background: "#fff",
  color: "#333",
};

const tbSelectStyle: React.CSSProperties = {
  height: 24,
  fontSize: 12,
  border: "1px solid #d0d5dd",
  borderRadius: 3,
  background: "#fff",
  color: "#333",
};

const sepStyle: React.CSSProperties = {
  display: "inline-block",
  width: 1,
  height: 18,
  margin: "0 3px",
  background: "#d0d5dd",
};

const colorInputStyle: React.CSSProperties = {
  width: 0,
  height: 0,
  padding: 0,
  border: "none",
  opacity: 0,
  position: "absolute",
};

export default SnHtmlTemplateEditor;
